import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useCallback, useEffect, useRef, useState } from "react";
import { CLASS_PAGE, CLASS_LIST_DEFAULT_ROWS } from "../../constants/app-constants";
import { getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { translate } from "../../localization/translateService";
import { getClassList } from "../../services/classService";
import useCurrency from "../../hook/useCurrency";
import { convertCurrency } from "../../helpers/currency";
import ClassFilter from "./ClassFilter";
import ClassList from "./ClassList";
import ClassListSearchSort from "./ClassListSearchSort";

const PAGE_TRANSLATION_KEYS = {
  filterTitle: "BUTTON.FILTER",
  searchButtonLabel: "BUTTON.SEARCH",
  searchPlaceholder: "COURSE_MANAGEMENT.SEARCH.PLACEHOLDER",
};

const CLASS_ITEM_TRANSLATION_KEYS = {
  ADD_SUCCESS: "UMBRACO_LANDING.VOD.ADD_TO_CART_SUCCESS",
  ADD_FAIL: "UMBRACO_LANDING.VOD.ADD_TO_CART_FAIL",
  PDF_AVAILABLE: "UMBRACO_LANDING.VOD.PDF_AVAILABLE",
  freeLabel: "COURSES.LABELS.FREE",
  boughtLabel: "COURSES.LABELS.PURCHASED",
  removeItemSuccess: "SHOPPING_CARTS.LABELS.REMOVE_ITEM_SUCCESS",
  removeItemLabel: "SHOPPING_CARTS.LABELS.REMOVE_ITEM",
  removeCartItemHeader: "SHOPPING_CARTS.LABELS.REMOVE_ITEM_HEADER",
  cancelButtonLabel: "BUTTON.CANCEL",
  confirmButtonLabel: "BUTTON.CONFIRM",
  START_DATE: "TITLE.START_DATE",
  END_DATE: "TITLE.END_DATE",
  INSTRUCTOR: "TITLE.INSTRUCTOR",
  PROCESS: "CLASS.LABELS.PROCESS",
};

const SORT_TRANSLATION_KEYS = {
  sortByHeader: "CLASS.LABELS.SORT_BY",
  upcomingLabel: "CLASS.LABELS.UPCOMING_SESSION",
  newestLabel: "CLASS.LABELS.NEWEST",
  popularLabel: "COURSE_MANAGEMENT.SORT.POPULAR",
  ratingLabel: "COURSE_MANAGEMENT.SORT.BY_RATING",
};

const SORT_OPTIONS = [
  { value: "upcoming", field: "NextSessionStartTime", order: 1 },
  { value: "newest", field: "CreatedTime", order: -1 },
  { value: "price_lowest", field: "Price", order: 1 },
  { value: "price_highest", field: "Price", order: -1 },
];

const TIME_OF_DAY_TRANSLATION_KEYS = {
  all: "COURSE_LMS.LABELS.ALL",
  morning: "CLASS.LABELS.MORNING",
  afternoon: "CLASS.LABELS.AFTERNOON",
  evening: "CLASS.LABELS.EVENING",
};

// value "" = All (no time-of-day filter). Single-select.
const TIME_OF_DAY_OPTIONS = [
  { value: "", labelKey: "all" },
  { value: "morning", labelKey: "morning" },
  { value: "afternoon", labelKey: "afternoon" },
  { value: "evening", labelKey: "evening" },
];

const CLASS_LIST_FILTER_PARAMS = [
  CLASS_PAGE.PAGE,
  CLASS_PAGE.ROWS,
  CLASS_PAGE.SEARCH,
  CLASS_PAGE.SORT_BY,
  CLASS_PAGE.TOPIC,
  CLASS_PAGE.RATING,
  CLASS_PAGE.STATUS,
  CLASS_PAGE.TIME_OF_DAY,
  CLASS_PAGE.START_TUITION,
  CLASS_PAGE.END_TUITION,
];

const RESET_FILTER_STATE = {
  selectedTopic: [],
  selectedRating: [],
  selectedStatus: [],
  selectedTimeOfDay: "",
  selectedPrice: null,
};

export default function ClassListPage({ props }) {
  const [pageTranslation, setPageTranslation] = useState({
    filterTitle: "",
    searchButtonLabel: "",
    searchPlaceholder: "",
  });

  const [sortTranslationData, setSortTranslationData] = useState(null);
  const [sortOptions, setSortOptions] = useState([]);
  const [timeOfDayOptions, setTimeOfDayOptions] = useState([]);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(CLASS_LIST_DEFAULT_ROWS);
  const [totalRecords, setTotalRecords] = useState(0);
  const [classListData, setClassListData] = useState(null);

  const [selectedPrice, setSelectedPrice] = useState(null);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]?.value ?? null);
  const [selectedTopic, setSelectedTopic] = useState([]);
  const [selectedRating, setSelectedRating] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState("");
  const [searchText, setSearchText] = useState("");
  const [hasInitialUrlState, setHasInitialUrlState] = useState(false);
  const [itemTranslations, setItemTranslations] = useState({
    ADD_SUCCESS: "",
    ADD_FAIL: "",
    PDF_AVAILABLE: "",
    freeLabel: "",
    boughtLabel: "",
    removeItemSuccess: "",
    removeItemLabel: "",
    removeCartItemHeader: "",
    cancelButtonLabel: "",
    confirmButtonLabel: "",
    START_DATE: "",
    END_DATE: "",
    INSTRUCTOR: "",
    PROCESS: "",
  });

  const toastRef = useRef(null);
  const fetchClassListRef = useRef(null);
  const skipFetchOnFirstChangeRef = useRef(false);

  // Currency handling (align with All Tutors behavior)
  const { defaultCurrency, ratesData, target: currentCurrency } = useCurrency();

  const getInitialStateFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rowsFromUrl = parseInt(urlParams.get(CLASS_PAGE.ROWS), 10) || CLASS_LIST_DEFAULT_ROWS;
    const pageFromUrl = parseInt(urlParams.get(CLASS_PAGE.PAGE), 10) || 1;
    const startTuition = urlParams.get(CLASS_PAGE.START_TUITION);
    const endTuition = urlParams.get(CLASS_PAGE.END_TUITION);

    let selectedPriceFromUrl = null;
    if (startTuition && endTuition) {
      const minDefault = parseInt(startTuition, 10);
      const maxDefault = parseInt(endTuition, 10);

      if (defaultCurrency && currentCurrency && ratesData) {
        // Convert stored default-currency range back to current display currency
        const minDisplay = convertCurrency(minDefault, defaultCurrency, currentCurrency, ratesData);
        const maxDisplay = convertCurrency(maxDefault, defaultCurrency, currentCurrency, ratesData);
        selectedPriceFromUrl = { min: minDisplay, max: maxDisplay };
      } else {
        // Fallback: use raw values if currency data not ready
        selectedPriceFromUrl = { min: minDefault, max: maxDefault };
      }
    }

    return {
      first: (pageFromUrl - 1) * rowsFromUrl,
      rows: rowsFromUrl,
      searchText: urlParams.get(CLASS_PAGE.SEARCH) ?? "",
      sortBy: urlParams.get(CLASS_PAGE.SORT_BY) || (SORT_OPTIONS[0]?.value ?? null),
      selectedTopic: urlParams.get(CLASS_PAGE.TOPIC) ? urlParams.get(CLASS_PAGE.TOPIC).split(",").filter(Boolean) : [],
      selectedRating: urlParams.get(CLASS_PAGE.RATING)
        ? urlParams
            .get(CLASS_PAGE.RATING)
            .split(",")
            .map((v) => (v.trim() ? Number(v) : null))
            .filter((v) => v != null && !Number.isNaN(v))
        : [],
      selectedStatus: urlParams.get(CLASS_PAGE.STATUS)
        ? urlParams
            .get(CLASS_PAGE.STATUS)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [],
      selectedTimeOfDay: urlParams.get(CLASS_PAGE.TIME_OF_DAY)?.trim() || "",
      selectedPrice: selectedPriceFromUrl,
    };
  }, [defaultCurrency, currentCurrency, ratesData]);

  useEffect(() => {
    // Đợi có thông tin currency rồi mới init để slider giá hiển thị đúng
    if (!defaultCurrency || !currentCurrency || !ratesData) {
      return;
    }
    const init = getInitialStateFromURL();
    setFirst(init.first);
    setRows(init.rows);
    setSearchText(init.searchText);
    setSortBy(init.sortBy);
    setSelectedTopic(init.selectedTopic);
    setSelectedRating(init.selectedRating);
    setSelectedStatus(init.selectedStatus);
    setSelectedTimeOfDay(init.selectedTimeOfDay);
    setSelectedPrice(init.selectedPrice);
    setHasInitialUrlState(true);
  }, [getInitialStateFromURL, defaultCurrency, currentCurrency, ratesData]);

  useEffect(() => {
    const loadItemTranslations = async () => {
      try {
        const [
          ADD_SUCCESS,
          ADD_FAIL,
          PDF_AVAILABLE,
          freeLabel,
          boughtLabel,
          removeItemSuccess,
          removeItemLabel,
          removeCartItemHeader,
          cancelButtonLabel,
          confirmButtonLabel,
          START_DATE,
          END_DATE,
          INSTRUCTOR,
          PROCESS,
        ] = await Promise.all([
          translate(CLASS_ITEM_TRANSLATION_KEYS.ADD_SUCCESS, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.ADD_FAIL, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.PDF_AVAILABLE, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.freeLabel, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.boughtLabel, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.removeItemSuccess, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.removeItemLabel, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.removeCartItemHeader, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.cancelButtonLabel, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.confirmButtonLabel, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.START_DATE, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.END_DATE, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.INSTRUCTOR, true),
          translate(CLASS_ITEM_TRANSLATION_KEYS.PROCESS, true),
        ]);
        setItemTranslations({
          ADD_SUCCESS,
          ADD_FAIL,
          PDF_AVAILABLE,
          freeLabel,
          boughtLabel,
          removeItemSuccess,
          removeItemLabel,
          removeCartItemHeader,
          cancelButtonLabel,
          confirmButtonLabel,
          START_DATE,
          END_DATE,
          INSTRUCTOR,
          PROCESS,
        });
      } catch {}
    };
    loadItemTranslations();
  }, []);

  useEffect(() => {
    const loadPageTranslation = async () => {
      try {
        const [filterTitle, searchButtonLabel, searchPlaceholder] = await Promise.all([
          translate(PAGE_TRANSLATION_KEYS.filterTitle, true),
          translate(PAGE_TRANSLATION_KEYS.searchButtonLabel, true),
          translate(PAGE_TRANSLATION_KEYS.searchPlaceholder, true),
        ]);
        setPageTranslation({ filterTitle, searchButtonLabel, searchPlaceholder });
      } catch {}
    };
    loadPageTranslation();
  }, []);

  useEffect(() => {
    const loadSortTranslation = async () => {
      try {
        const [sortByHeader, newestLabel, popularLabel, ratingLabel, upcomingLabel] = await Promise.all([
          translate(SORT_TRANSLATION_KEYS.sortByHeader, true),
          translate(SORT_TRANSLATION_KEYS.newestLabel, true),
          translate(SORT_TRANSLATION_KEYS.popularLabel, true),
          translate(SORT_TRANSLATION_KEYS.ratingLabel, true),
          translate(SORT_TRANSLATION_KEYS.upcomingLabel, true),
        ]);
        const options = [
          { value: "upcoming", label: upcomingLabel },
          { value: "newest", label: newestLabel },
          { value: "rating", label: ratingLabel },
        ];
        setSortOptions(options);
        setSortTranslationData({
          sortByHeader,
          options,
        });
      } catch {}
    };
    loadSortTranslation();
  }, []);

  useEffect(() => {
    const loadTimeOfDayTranslation = async () => {
      try {
        const [all, morning, afternoon, evening] = await Promise.all([
          translate(TIME_OF_DAY_TRANSLATION_KEYS.all, true),
          translate(TIME_OF_DAY_TRANSLATION_KEYS.morning, true),
          translate(TIME_OF_DAY_TRANSLATION_KEYS.afternoon, true),
          translate(TIME_OF_DAY_TRANSLATION_KEYS.evening, true),
        ]);
        const labelByKey = { all, morning, afternoon, evening };
        setTimeOfDayOptions(TIME_OF_DAY_OPTIONS.map((o) => ({ value: o.value, label: labelByKey[o.labelKey] ?? o.value })));
      } catch {}
    };
    loadTimeOfDayTranslation();
  }, []);

  const updateUrlParams = useCallback(
    (paginationParams, filterRequest) => {
      const url = new URL(window.location);
      const params = url.searchParams;
      CLASS_LIST_FILTER_PARAMS.forEach((param) => params.delete(param));
      params.set(CLASS_PAGE.PAGE, (Math.floor(paginationParams.first / rows) + 1).toString());
      params.set(CLASS_PAGE.ROWS, rows.toString());
      if (filterRequest.search) params.set(CLASS_PAGE.SEARCH, filterRequest.search);
      if (filterRequest.sortBy) params.set(CLASS_PAGE.SORT_BY, filterRequest.sortBy);
      if (filterRequest.topic) params.set(CLASS_PAGE.TOPIC, filterRequest.topic);
      if (filterRequest.rating && String(filterRequest.rating).trim()) params.set(CLASS_PAGE.RATING, filterRequest.rating);
      if (filterRequest.status && String(filterRequest.status).trim()) params.set(CLASS_PAGE.STATUS, filterRequest.status);
      if (filterRequest.timeOfDay && String(filterRequest.timeOfDay).trim()) params.set(CLASS_PAGE.TIME_OF_DAY, filterRequest.timeOfDay);
      if (filterRequest.startTuition != null) params.set(CLASS_PAGE.START_TUITION, String(filterRequest.startTuition));
      if (filterRequest.endTuition != null) params.set(CLASS_PAGE.END_TUITION, String(filterRequest.endTuition));
      window.history.pushState({}, "", url);
    },
    [rows],
  );

  const buildFilterRequest = useCallback(
    (overrides = {}) => {
      const effectiveSelectedPrice = "selectedPrice" in overrides ? overrides.selectedPrice : selectedPrice;

      let startTuition = null;
      let endTuition = null;

      if (effectiveSelectedPrice && defaultCurrency && currentCurrency && ratesData) {
        // Convert from current display currency to default currency (giống All Tutors)
        const minDefault = convertCurrency(effectiveSelectedPrice.min, currentCurrency, defaultCurrency, ratesData);
        const maxDefault = convertCurrency(effectiveSelectedPrice.max, currentCurrency, defaultCurrency, ratesData);
        startTuition = Math.round(minDefault);
        endTuition = Math.round(maxDefault);
      } else if (effectiveSelectedPrice) {
        // Fallback nếu chưa có ratesData: dùng trực tiếp
        startTuition = effectiveSelectedPrice.min;
        endTuition = effectiveSelectedPrice.max;
      }

      const effectiveRating = "selectedRating" in overrides ? overrides.selectedRating : selectedRating;
      const effectiveStatus = "selectedStatus" in overrides ? overrides.selectedStatus : selectedStatus;
      const effectiveTimeOfDay = "selectedTimeOfDay" in overrides ? overrides.selectedTimeOfDay : selectedTimeOfDay;
      return {
        search: (overrides.searchText ?? searchText)?.trim() || null,
        sortBy: overrides.sortBy ?? sortBy ?? null,
        topic: Array.isArray(overrides.selectedTopic ?? selectedTopic) ? (overrides.selectedTopic ?? selectedTopic).join(",") : "",
        rating: Array.isArray(effectiveRating) ? effectiveRating.join(",") : (effectiveRating ?? ""),
        status: Array.isArray(effectiveStatus) ? effectiveStatus.join(",") : (effectiveStatus ?? ""),
        timeOfDay: Array.isArray(effectiveTimeOfDay) ? effectiveTimeOfDay.join(",") : (effectiveTimeOfDay ?? ""),
        timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
        startTuition,
        endTuition,
      };
    },
    [searchText, sortBy, selectedTopic, selectedRating, selectedStatus, selectedTimeOfDay, selectedPrice, defaultCurrency, currentCurrency, ratesData],
  );

  const fetchClassList = useCallback(
    async (filterOverrides, firstOverride) => {
      const currentFirst = firstOverride !== undefined ? firstOverride : first;
      const currentSortBy = filterOverrides?.sortBy ?? sortBy;
      const sortOption = SORT_OPTIONS.find((opt) => opt.value === currentSortBy);
      const multiSortMeta = sortOption ? [{ field: sortOption.field, order: sortOption.order }] : [];
      const paginationParams = {
        first: currentFirst,
        rows,
        filters: null,
        multiSortMeta,
      };
      const filterRequest = buildFilterRequest(filterOverrides);
      updateUrlParams(paginationParams, filterRequest);
      try {
        const res = await getClassList(paginationParams, filterRequest);
        setClassListData(res?.data ?? null);
        setTotalRecords(res?.pagingInfo?.totalRecordCount ?? 0);
      } catch {
        setClassListData(null);
        setTotalRecords(0);
      }
    },
    [first, rows, sortBy, buildFilterRequest, updateUrlParams],
  );

  fetchClassListRef.current = fetchClassList;

  useEffect(() => {
    if (!hasInitialUrlState) return;
    if (skipFetchOnFirstChangeRef.current) {
      skipFetchOnFirstChangeRef.current = false;
      return;
    }
    fetchClassListRef.current?.();
  }, [hasInitialUrlState, first]);

  const onFilterClick = useCallback(() => {
    skipFetchOnFirstChangeRef.current = true;
    setFirst(0);
    fetchClassList(undefined, 0);
  }, [fetchClassList]);

  const onSearchClear = useCallback(() => {
    setSearchText("");
    skipFetchOnFirstChangeRef.current = true;
    setFirst(0);
    fetchClassList({ searchText: "" }, 0);
  }, [fetchClassList]);

  const onFilterReset = useCallback(() => {
    setSelectedPrice(null);
    setSelectedTopic([]);
    setSelectedRating([]);
    setSelectedStatus([]);
    setSelectedTimeOfDay("");
    skipFetchOnFirstChangeRef.current = true;
    setFirst(0);
    fetchClassList({ ...RESET_FILTER_STATE, sortBy }, 0);
  }, [fetchClassList, sortBy]);

  const onSortOptionChange = useCallback(
    (value) => {
      setSortBy(value);
      skipFetchOnFirstChangeRef.current = true;
      setFirst(0);
      fetchClassList({ sortBy: value }, 0);
    },
    [fetchClassList],
  );

  const onFilterPriceChange = useCallback(
    (value) => {
      setSelectedPrice(value);
      skipFetchOnFirstChangeRef.current = true;
      setFirst(0);
      fetchClassList({ selectedPrice: value }, 0);
    },
    [fetchClassList],
  );

  const onFilterTopicChange = useCallback(
    (value) => {
      setSelectedTopic(value ?? []);
      skipFetchOnFirstChangeRef.current = true;
      setFirst(0);
      fetchClassList({ selectedTopic: value ?? [] }, 0);
    },
    [fetchClassList],
  );

  const onFilterRatingChange = useCallback(
    (value) => {
      const next = value ?? [];
      setSelectedRating(next);
      skipFetchOnFirstChangeRef.current = true;
      setFirst(0);
      fetchClassList({ selectedRating: next }, 0);
    },
    [fetchClassList],
  );

  const onFilterStatusChange = useCallback(
    (value) => {
      const next = value ?? [];
      setSelectedStatus(next);
      skipFetchOnFirstChangeRef.current = true;
      setFirst(0);
      fetchClassList({ selectedStatus: next }, 0);
    },
    [fetchClassList],
  );

  const onFilterTimeOfDayChange = useCallback(
    (value) => {
      const next = value ?? "";
      setSelectedTimeOfDay(next);
      skipFetchOnFirstChangeRef.current = true;
      setFirst(0);
      fetchClassList({ selectedTimeOfDay: next }, 0);
    },
    [fetchClassList],
  );

  const onItemClick = useCallback((course) => {
    const language = getI18nLanguageFromLocalStorage();
    const url = `/${language}/courses/${course.publicId}`;
    window.open(url, "_blank");
  }, []);

  const onPageChange = useCallback((e) => {
    setFirst(e.first);
    const el = document.getElementById("class-list-item");
    if (el) {
      const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const y = el.getBoundingClientRect().top + window.pageYOffset - 8.6 * remInPx;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  return (
    <div className="flex flex-column align-content-center justify-content-center main-content-wrapper">
      <div className="class-list-layout flex flex-column md:flex-row gap-3 w-full">
        <div className="class-list-filter">
          <div className="md:hidden flex w-full mb-3">
            <ClassListSearchSort
              searchText={searchText}
              searchPlaceholder={pageTranslation.searchPlaceholder}
              onSearchChange={setSearchText}
              onSearchClear={onSearchClear}
              onSearchSubmit={onFilterClick}
              timeOfDayOptions={timeOfDayOptions}
              selectedTimeOfDay={selectedTimeOfDay}
              onTimeOfDayChange={onFilterTimeOfDayChange}
            />
          </div>
          <h5 className="keduall-typography text-default-txt heading-h5-semibold-600">{pageTranslation.filterTitle}</h5>
          <Divider className="keduall-divider mx-0 my-3" />
          <ClassFilter
            translationData={sortTranslationData}
            sortOptions={sortOptions}
            selectedPrice={selectedPrice}
            sortBy={sortBy}
            selectedTopic={selectedTopic}
            selectedRating={selectedRating}
            selectedStatus={selectedStatus}
            selectedTimeOfDay={selectedTimeOfDay}
            onFilterPriceChange={onFilterPriceChange}
            onSortOptionChange={onSortOptionChange}
            onFilterTopicChange={onFilterTopicChange}
            onFilterRatingChange={onFilterRatingChange}
            onFilterStatusChange={onFilterStatusChange}
            onFilterTimeOfDayChange={onFilterTimeOfDayChange}
            onFilterReset={onFilterReset}
          />
          <Divider className="keduall-divider md:hidden mt-5" />
        </div>
        <div className="class-list-content flex-1 min-w-0">
          <div className="hidden md:flex w-full mb-3">
            <ClassListSearchSort
              searchText={searchText}
              searchPlaceholder={pageTranslation.searchPlaceholder}
              onSearchChange={setSearchText}
              onSearchClear={onSearchClear}
              onSearchSubmit={onFilterClick}
              timeOfDayOptions={timeOfDayOptions}
              selectedTimeOfDay={selectedTimeOfDay}
              onTimeOfDayChange={onFilterTimeOfDayChange}
            />
          </div>
          <ClassList
            id="class-list-item"
            listData={classListData}
            first={first}
            rows={rows}
            total={totalRecords}
            onPageChange={onPageChange}
            onItemClick={onItemClick}
            translations={itemTranslations}
            toast={toastRef}
          />
        </div>
      </div>
      <Toast ref={toastRef} position="bottom-right" className="keduall-toast" />
      <ConfirmDialog className="keduall-confirm-dialog" draggable={false} />
    </div>
  );
}
